class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initClearCartButton();
  }

  initClearCartButton() {
    // Use event delegation instead of direct binding
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('cart-clear-button')) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Clear cart button clicked via delegation');
        this.clearCart();
      }
    });

    // Also try to find button directly
    const clearButton = this.querySelector('.cart-clear-button');
    if (clearButton) {
      console.log('Clear button found directly, adding listener');
      clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        console.log('Clear cart button clicked directly');
        this.clearCart();
      });
    } else {
      console.log('Clear button not found during init');
    }
  }

  async clearCart() {
    console.log('Clear cart clicked');
    const clearButton = this.querySelector('.cart-clear-button');
    if (!clearButton) return;

    // Show loading state
    clearButton.setAttribute('disabled', '');
    const originalText = clearButton.textContent;
    clearButton.textContent = 'Clearing...';

    try {
      // Clear cart via Shopify Cart API
      const cartUrl = window.routes?.cart_url || '/cart';
      const clearResponse = await fetch(`${cartUrl}/clear.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!clearResponse.ok) throw new Error(`HTTP ${clearResponse.status}`);

      // Reload empty cart content from server (like cart-remove-button does)
      await this.reloadEmptyCartContent();

      console.log('Cart cleared successfully');
    } catch (error) {
      console.error('Clear cart failed:', error);
    } finally {
      clearButton.removeAttribute('disabled');
      clearButton.textContent = originalText;
    }
  }

  async reloadEmptyCartContent() {
    try {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}?section_id=cart-drawer`);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Get the entire cart drawer content from server
      const newCartDrawer = html.querySelector('#CartDrawer');
      const currentCartDrawer = this.querySelector('#CartDrawer');

      if (newCartDrawer && currentCartDrawer) {
        // Replace entire content to get proper empty state
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      // Set empty state class
      this.classList.add('is-empty');

      // Clear recommendations when cart is empty
      const recommendationsContainer = this.querySelector('#CartDrawer-Recommendations');
      if (recommendationsContainer) {
        recommendationsContainer.innerHTML = '';
      }

      // Force update header cart counters to 0
      this.updateHeaderCountersToZero();

      // Re-bind all event listeners after content reload
      this.rebindEventListeners();
    } catch (error) {
      console.error('Failed to reload empty cart content:', error);
      // Fallback to manual empty state
      this.showEmptyState();
    }
  }

  updateHeaderCountersToZero() {
    // Update all cart count badges to 0
    const headerCartCounters = document.querySelectorAll('.cart-count-badge');
    const drawerCartCounters = document.querySelectorAll('.drawer__title-counter');

    headerCartCounters.forEach((counter) => {
      counter.textContent = '0';
      counter.classList.add('hidden'); // Hide when 0
    });

    drawerCartCounters.forEach((counter) => {
      counter.textContent = '0';
    });

    // Hide cart bubble completely
    const countBubble = document.querySelector('.cart-count-bubble');
    if (countBubble) {
      countBubble.classList.add('hidden');
    }

    // Also update via global function as backup
    if (window.updateCartCounters) {
      setTimeout(() => window.updateCartCounters(), 100);
    }

    console.log('Header counters updated to 0');
  }

  rebindEventListeners() {
    // Re-bind overlay click
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    // Re-bind close button click with preventDefault
    const closeButtons = this.querySelectorAll('.drawer__close');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });

    // Re-initialize clear button
    this.initClearCartButton();

    // Re-bind summary accessibility if exists
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    console.log('Event listeners rebound after content reload');
  }

  showEmptyState() {
    // Add empty state class
    this.classList.add('is-empty');

    // Hide all structure with items, header and footer
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    const drawerFooter = this.querySelector('.drawer__footer');
    const clearButton = this.querySelector('.cart-clear-button');

    if (cartDrawerItems) cartDrawerItems.style.display = 'none';
    if (drawerFooter) drawerFooter.style.display = 'none';
    if (clearButton) clearButton.style.display = 'none';

    // Show ready-made empty structure
    const drawerInnerEmpty = this.querySelector('.drawer__inner-empty');
    if (drawerInnerEmpty) {
      drawerInnerEmpty.style.display = 'block';
    }

    // Update title counter
    const titleCounter = this.querySelector('.drawer__title-counter');
    if (titleCounter) {
      titleCounter.textContent = '0';
    }

    // Hide cart bubble
    const countBubble = document.querySelector('.cart-count-bubble');
    if (countBubble) {
      countBubble.classList.add('hidden');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Close quick-cart-drawer if it's open
    const quickCartDrawer = document.querySelector('quick-cart-drawer');
    if (quickCartDrawer && quickCartDrawer.classList.contains('is--open')) {
      quickCartDrawer.close();
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Force animation trigger
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const isOpen = event.currentTarget.closest('details').hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', !isOpen);
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  async renderContents(parsedState) {
    console.log('=== RENDER CONTENTS DEBUG ===');
    console.log('Full parsedState:', parsedState);
    console.log('parsedState.item_count:', parsedState.item_count);
    console.log('parsedState.items:', parsedState.items);
    console.log('parsedState.items length:', parsedState.items?.length);
    console.log('parsedState.sections:', parsedState.sections);
    console.log('================================');

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    // Remove is-empty from cart-drawer itself
    this.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

      if (!sectionElement) {
        console.warn(`Section element not found: ${section.selector || section.id}`);
        return;
      }

      const htmlContent = parsedState.sections?.[section.id];
      if (!htmlContent) {
        console.warn(`No HTML content for section: ${section.id}`);
        return;
      }

      const newHTML = this.getSectionInnerHTML(htmlContent, section.selector);
      if (newHTML) {
        sectionElement.innerHTML = newHTML;
      }
    });

    // Update counters and load recommendations after rendering
    setTimeout(async () => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

      // Re-initialize clear button after rendering
      this.initClearCartButton();

      // Show cart bubble when items are added
      const itemCount = parsedState.item_count || parsedState.items?.length || 0;
      if (itemCount > 0) {
        const countBubble = document.querySelector('.cart-count-bubble');
        if (countBubble) {
          countBubble.classList.remove('hidden');
        }

        // Update cart count badges manually
        const cartCountBadges = document.querySelectorAll('.cart-count-badge');
        cartCountBadges.forEach((badge) => {
          badge.textContent = itemCount;
          badge.classList.remove('hidden');
        });
      }

      // Update global counters
      if (window.updateCartCounters) {
        await window.updateCartCounters();
      }

      // Debug and load recommendations when cart has items
      console.log('Checking for recommendations...');

      // Get full cart data instead of relying on parsedState
      try {
        const cartUrl = window.routes?.cart_url || '/cart';
        const cartResponse = await fetch(`${cartUrl}.json`);
        const fullCartData = await cartResponse.json();

        console.log('Full cart data from API:', fullCartData);
        console.log('Full cart item_count:', fullCartData.item_count);
        console.log('Full cart items:', fullCartData.items);

        const itemCount = fullCartData.item_count || fullCartData.items?.length || 0;
        console.log('Final item count for recommendations:', itemCount);

        if (itemCount > 0) {
          console.log('Loading recommendations with full cart data...');
          await this.loadCartRecommendations(fullCartData);
        } else {
          console.log('No items in cart, skipping recommendations');
        }
      } catch (error) {
        console.error('Failed to get full cart data:', error);

        // Fallback to parsedState
        const itemCount = parsedState.item_count || parsedState.items?.length || 0;
        console.log('Fallback - checking parsedState item count:', itemCount);

        if (itemCount > 0) {
          console.log('Attempting to load recommendations with parsedState...');
          await this.loadCartRecommendations(parsedState);
        }
      }

      this.open();
    });
  }

  async loadCartRecommendations(parsedState) {
    try {
      console.log('=== Loading recommendations ===');

      const recommendationsContainer = this.querySelector('#CartDrawer-Recommendations');
      if (!recommendationsContainer) {
        console.log('Recommendations container not found');
        return;
      }

      // Build product IDs from cart for recommendations
      const productIds = parsedState.items?.map((item) => item.product_id) || [];
      if (productIds.length === 0) {
        console.log('No product IDs found');
        return;
      }

      // Request recommendations based on cart items
      const recommendationsUrl = `${window.routes?.product_recommendations_url || '/recommendations/products'}?product_id=${productIds[0]}&limit=8&intent=related&section_id=cart-recommendations`;

      console.log('Loading recommendations from:', recommendationsUrl);

      const response = await fetch(recommendationsUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      console.log('Received HTML length:', html.length);

      recommendationsContainer.innerHTML = html;
      console.log('✅ Recommendations loaded successfully');

      // Initialize add to cart buttons for recommendations
      this.initRecommendationButtons();
    } catch (error) {
      console.error('Failed to load cart recommendations:', error);
    }
  }

  initRecommendationButtons() {
    // Handle add to cart buttons in recommendations
    this.querySelectorAll('[data-recommendation-variant-id]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        const variantId = button.dataset.recommendationVariantId;
        if (variantId) {
          await this.addRecommendationToCart(variantId, button);
        }
      });
    });
  }

  async addRecommendationToCart(variantId, button) {
    try {
      // Show loading state
      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.setAttribute('disabled', '');

      // Use the same approach as product-card.js
      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : {
              method: 'POST',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
            };

      // Remove Content-Type to let browser set it for FormData
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Add sections to update cart drawer
      const sectionsToRender = this.getSectionsToRender();
      const sectionNames = sectionsToRender.map((section) => section.section || section.id);
      formData.append('sections', sectionNames.join(','));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
      console.log('Adding recommendation with config:', config);

      const response = await fetch(cartAddUrl, config);

      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Add to cart result:', result);

      if (result.errors) {
        console.error('Add recommendation error:', result.errors);
        alert('Error: ' + result.errors);
        return;
      }

      // Update cart drawer with new content
      await this.renderContents(result);

      // Also update header counters
      if (window.updateCartCounters) {
        await window.updateCartCounters();
      }

      // Show cart bubble in header
      const countBubble = document.querySelector('.cart-count-bubble');
      if (countBubble) {
        countBubble.classList.remove('hidden');
      }

      console.log('Recommendation added to cart successfully');
    } catch (error) {
      console.error('Failed to add recommendation to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    } finally {
      // Reset button state
      if (button) {
        button.textContent = originalText;
        button.removeAttribute('disabled');
      }
    }
  }

  async updateRecommendationsAfterCartChange() {
    try {
      console.log('Updating recommendations after cart change...');

      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.json`);
      const cartData = await response.json();

      const itemCount = cartData.item_count || cartData.items?.length || 0;
      console.log('Cart change - item count:', itemCount);

      if (itemCount > 0) {
        await this.loadCartRecommendations(cartData);
      } else {
        // Clear recommendations if cart is empty
        const recommendationsContainer = this.querySelector('#CartDrawer-Recommendations');
        if (recommendationsContainer) {
          recommendationsContainer.innerHTML = '';
          console.log('Recommendations cleared - cart is empty');
        }
      }
    } catch (error) {
      console.error('Failed to update recommendations after cart change:', error);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      // Removed cart-icon-bubble to prevent header overwrite issues
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Check if CartItems exists before inheritance
let CartItemsBase = HTMLElement;
if (typeof CartItems !== 'undefined') {
  CartItemsBase = CartItems;
} else if (typeof window.CartItems !== 'undefined') {
  CartItemsBase = window.CartItems;
} else {
  console.warn('CartItems class not found, CartDrawerItems will extend HTMLElement directly');
}

class CartDrawerItems extends CartItemsBase {
  constructor() {
    super();

    // If not inheriting from CartItems, add basic functionality
    if (CartItemsBase === HTMLElement) {
      this.initBasicFunctionality();
    }
  }

  initBasicFunctionality() {
    this.lineItemStatusElement = document.getElementById('CartDrawer-LineItemStatus');

    // Simple debounce
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (typeof this.validateQuantity === 'function') {
      this.validateQuantity(event);
    }
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (typeof this.updateQuantity === 'function') {
      this.updateQuantity(index, inputValue, event, event.target.getAttribute('name'));
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Load recommendations on page load if cart has items
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(async () => {
    try {
      console.log('Checking for recommendations on page load...');

      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.json`);
      const cartData = await response.json();

      console.log('Page load cart data:', cartData);

      const itemCount = cartData.item_count || cartData.items?.length || 0;
      console.log('Page load item count:', itemCount);

      if (itemCount > 0) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.loadCartRecommendations === 'function') {
          console.log('Loading recommendations on page load...');
          await cartDrawer.loadCartRecommendations(cartData);
        } else {
          console.log('Cart drawer not ready for page load recommendations');
        }
      }
    } catch (error) {
      console.log('Could not load recommendations on page load:', error);
    }
  }, 1000); // Wait 1 second for cart-drawer to be ready
});

// Listen for cart changes (when items are removed via cart-remove-button)
document.addEventListener('click', async (event) => {
  if (event.target.closest('cart-remove-button')) {
    console.log('Cart remove button clicked - will update recommendations after change');

    // Wait for cart update to complete, then update recommendations
    setTimeout(async () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.updateRecommendationsAfterCartChange === 'function') {
        await cartDrawer.updateRecommendationsAfterCartChange();
      }
    }, 1500); // Wait for cart update to complete
  }

  // Also listen for quantity button changes (plus/minus buttons)
  if (
    event.target.classList.contains('quantity__button') ||
    event.target.closest('.quantity__button') ||
    event.target.name === 'plus' ||
    event.target.name === 'minus'
  ) {
    console.log('Quantity button clicked - will update recommendations after change');

    // Wait for cart update to complete, then update recommendations
    setTimeout(async () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.updateRecommendationsAfterCartChange === 'function') {
        await cartDrawer.updateRecommendationsAfterCartChange();
      }
    }, 1500); // Wait for cart update to complete
  }
});

// Listen for quantity input changes (when typing in quantity field)
document.addEventListener('change', async (event) => {
  if (event.target.classList.contains('quantity__input') && event.target.closest('cart-drawer')) {
    console.log('Quantity input changed - will update recommendations after change');

    // Wait for cart update to complete, then update recommendations
    setTimeout(async () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.updateRecommendationsAfterCartChange === 'function') {
        await cartDrawer.updateRecommendationsAfterCartChange();
      }
    }, 1500); // Wait for cart update to complete
  }
});
